import type { AIToolsConfig } from '@affine/core/modules/ai-button';
import { partition } from 'lodash-es';

import { getGroqApiKey, streamGroqToAffineUI } from '../../groq-stream';
import { toTextStream } from '../../provider/event-source';
import { createWorkspaceByokLocalLease } from './byok-local-lease';
import { type CopilotClient, Endpoint } from './copilot-client';

const TIMEOUT = 50000;

export type TextToTextOptions = {
  client: CopilotClient;
  sessionId: string;
  workspaceId?: string;
  content?: string;
  attachments?: (string | Blob | File)[];
  params?: Record<string, unknown>;
  timeout?: number;
  stream?: boolean;
  signal?: AbortSignal;
  retry?: boolean;
  endpoint?: Endpoint;
  actionId?: string;
  actionVersion?: string;
  runId?: string;
  isRootSession?: boolean;
  reasoning?: boolean;
  profileId?: string;
  modelId?: string;
  routeTargetId?: string;
  toolsConfig?: AIToolsConfig;
};

export type ToImageOptions = TextToTextOptions & {
  seed?: string;
};

async function resizeImage(blob: Blob | File): Promise<Blob | null> {
  let src = '';
  try {
    src = URL.createObjectURL(blob);
    const img = new Image();
    img.src = src;
    await new Promise(resolve => {
      img.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const scale = Math.min(1024 / img.width, 1024 / img.height);
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return await new Promise(resolve =>
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.8)
      );
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (src) URL.revokeObjectURL(src);
  }
  return null;
}

function jpegFileName(name: string) {
  const stem = name.replace(/\.[^./\\]+$/, '');
  return `${stem || 'image'}.jpg`;
}

interface CreateMessageOptions {
  client: CopilotClient;
  sessionId: string;
  content?: string;
  attachments?: (string | Blob | File)[];
  params?: Record<string, unknown>;
  timeout?: number;
  signal?: AbortSignal;
}

async function createMessage({
  client,
  sessionId,
  content,
  attachments,
  params,
  timeout,
  signal,
}: CreateMessageOptions): Promise<string> {
  const hasAttachments = attachments && attachments.length > 0;
  const options: Parameters<CopilotClient['createMessage']>[0] = {
    sessionId,
    content,
    params: params as Parameters<CopilotClient['createMessage']>[0]['params'],
  };

  if (hasAttachments) {
    const [stringAttachments, blobs] = partition(
      attachments,
      attachment => typeof attachment === 'string'
    ) as [string[], (Blob | File)[]];
    options.attachments = stringAttachments;
    options.blobs = (
      await Promise.all(
        blobs.map(async blob => {
          const file = blob.type.startsWith('image/')
            ? await resizeImage(blob)
            : blob;
          if (!file) return null;
          const resized = file !== blob;
          return new File(
            [file],
            resized
              ? jpegFileName(blob instanceof File ? blob.name : sessionId)
              : blob instanceof File
                ? blob.name
                : sessionId,
            { type: file.type }
          );
        })
      )
    ).filter(Boolean) as File[];
  }

  return await client.createMessage(options, { timeout, signal });
}

function cleanDocText(text: string): string {
  let clean = text.replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '').trim();
  clean = clean
    .replace(
      /^(?:(?:The user asks|As Cáo Ơi|We need to follow|Will produce answer|Thinking Process:|Internal reasoning:|Planning:)[^\n]*\n*)+/gi,
      ''
    )
    .trim();

  clean = clean.replace(
    /```(?:widget:)?(kpi|chart|tabs|json|[\w-]*)?\s*([\s\S]*?)```/gi,
    (fullMatch, _tag, rawContent) => {
      try {
        const parsed = JSON.parse(rawContent.trim());
        if (parsed.kpis && Array.isArray(parsed.kpis)) {
          let out = `\n### 📊 ${parsed.title || 'KPI Scorecard'}\n\n`;
          out += `| Metric | Target / Value | YoY Change |\n| :--- | :--- | :--- |\n`;
          for (const k of parsed.kpis) {
            out += `| **${k.label}** | **${k.value}** | ${k.change || '-'} |\n`;
          }
          return out + '\n';
        }
        if (
          parsed.type === 'bar' ||
          (Array.isArray(parsed.data) &&
            parsed.data.length > 0 &&
            parsed.data[0].label)
        ) {
          let out = `\n### 📈 ${parsed.title || 'Trend Overview'}\n\n`;
          out += `| Period / Category | Amount |\n| :--- | :--- |\n`;
          for (const d of parsed.data) {
            out += `| **${d.label}** | **${d.formatted || d.value}** |\n`;
          }
          return out + '\n';
        }
        if (Array.isArray(parsed.tabs)) {
          let out = `\n`;
          for (const tab of parsed.tabs) {
            out += `**${tab.label}**\n\n`;
          }
          return out;
        }
      } catch {
        // Not widget JSON
      }
      return fullMatch;
    }
  );

  return clean;
}

async function* stripThinkingFromStream(
  stream: AsyncIterable<string>,
  signal?: AbortSignal
) {
  let inThink = false;
  let rawAccumulated = '';
  let isHeaderProcessed = false;
  let codeBlockBuffer = '';
  let inCodeBlock = false;

  for await (const chunk of stream) {
    if (signal?.aborted) return;
    rawAccumulated += chunk;

    while (rawAccumulated.length > 0) {
      if (!inThink) {
        const startIdx = rawAccumulated.indexOf('<think>');
        if (startIdx !== -1) {
          if (startIdx > 0) {
            const before = rawAccumulated.substring(0, startIdx);
            rawAccumulated = rawAccumulated.substring(startIdx + 7);
            inThink = true;
            yield* processStreamChunk(before);
          } else {
            rawAccumulated = rawAccumulated.substring(7);
            inThink = true;
          }
        } else {
          const partial = rawAccumulated.match(/<t(?:h(?:i(?:n(?:k)?)?)?)?$/i);
          if (partial && partial.index !== undefined) {
            const safe = rawAccumulated.substring(0, partial.index);
            rawAccumulated = rawAccumulated.substring(partial.index);
            if (safe) yield* processStreamChunk(safe);
            break;
          } else {
            const safe = rawAccumulated;
            rawAccumulated = '';
            yield* processStreamChunk(safe);
          }
        }
      } else {
        const endIdx = rawAccumulated.indexOf('</think>');
        if (endIdx !== -1) {
          rawAccumulated = rawAccumulated.substring(endIdx + 8).replace(/^\n+/, '');
          inThink = false;
        } else {
          const partialEnd = rawAccumulated.match(/<\/(?:t(?:h(?:i(?:n(?:k)?)?)?)?)?$/i);
          if (partialEnd && partialEnd.index !== undefined) {
            rawAccumulated = rawAccumulated.substring(partialEnd.index);
          } else {
            rawAccumulated = '';
          }
          break;
        }
      }
    }
  }

  // Flush remaining buffers at end of stream
  if (!isHeaderProcessed && codeBlockBuffer) {
    const stripped = codeBlockBuffer.replace(
      /^(?:(?:The user asks|As Cáo Ơi|We need to follow|Will produce answer|Thinking Process|Internal reasoning|Planning:)[^\n]*\n*)+/i,
      ''
    );
    codeBlockBuffer = '';
    const cleaned = cleanDocText(stripped);
    if (cleaned) yield cleaned;
  } else if (codeBlockBuffer) {
    const cleaned = cleanDocText(codeBlockBuffer);
    if (cleaned) yield cleaned;
  }

  async function* processStreamChunk(text: string): AsyncGenerator<string> {
    if (!text) return;

    if (!isHeaderProcessed) {
      codeBlockBuffer += text;
      if (codeBlockBuffer.includes('\n\n') || codeBlockBuffer.length > 250) {
        isHeaderProcessed = true;
        const stripped = codeBlockBuffer.replace(
          /^(?:(?:The user asks|As Cáo Ơi|We need to follow|Will produce answer|Thinking Process|Internal reasoning|Planning:)[^\n]*\n*)+/i,
          ''
        );
        codeBlockBuffer = '';
        yield* yieldOrTransformBlocks(stripped);
      }
      return;
    }

    yield* yieldOrTransformBlocks(text);
  }

  async function* yieldOrTransformBlocks(text: string): AsyncGenerator<string> {
    if (inCodeBlock) {
      codeBlockBuffer += text;
      const endCode = codeBlockBuffer.indexOf('```');
      if (endCode !== -1) {
        inCodeBlock = false;
        const fullBlock = codeBlockBuffer.substring(0, endCode + 3);
        const remainder = codeBlockBuffer.substring(endCode + 3);
        codeBlockBuffer = '';
        yield cleanDocText(fullBlock);
        if (remainder) yield* yieldOrTransformBlocks(remainder);
      }
      return;
    }

    const startCode = text.indexOf('```');
    if (startCode !== -1) {
      const before = text.substring(0, startCode);
      if (before) yield before;
      codeBlockBuffer = text.substring(startCode);
      const secondCode = codeBlockBuffer.indexOf('```', 3);
      if (secondCode !== -1) {
        const fullBlock = codeBlockBuffer.substring(0, secondCode + 3);
        const remainder = codeBlockBuffer.substring(secondCode + 3);
        codeBlockBuffer = '';
        yield cleanDocText(fullBlock);
        if (remainder) yield* yieldOrTransformBlocks(remainder);
      } else {
        inCodeBlock = true;
      }
      return;
    }

    yield text;
  }
}

export function textToText({
  client,
  sessionId,
  workspaceId,
  content,
  attachments,
  params,
  stream,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint = Endpoint.StreamObject,
  actionId,
  actionVersion,
  runId,
  reasoning,
  profileId,
  modelId,
  routeTargetId,
  toolsConfig,
}: TextToTextOptions) {
  // If Groq API key is available, execute directly via Cáo Ơi Groq 120B engine!
  const groqKey = getGroqApiKey();
  if (groqKey) {
    let prompt = content || '';
    if (actionId) {
      if (actionId === 'summary') {
        prompt = `Hãy tóm tắt ngắn gọn, sắc bén và làm nổi bật các ý chính theo phong thái điều hành CEO cho nội dung sau (giữ ngôn ngữ tự nhiên của bài viết, ưu tiên tiếng Việt):\n\n${content}`;
      } else if (actionId === 'explain') {
        prompt = `Giải thích rõ ràng các khái niệm sau với góc nhìn ứng dụng thực chiến trong quản trị doanh nghiệp (ưu tiên tiếng Việt):\n\n${content}`;
      } else if (actionId === 'translate') {
        prompt = `Dịch nội dung sau sang ${params?.language || 'tiếng Việt'} một cách chuẩn xác, tự nhiên:\n\n${content}`;
      } else if (actionId === 'changeTone') {
        prompt = `Viết lại nội dung sau với văn phong ${params?.tone || 'chuyên nghiệp, thuyết phục, chuẩn phong thái lãnh đạo'}:\n\n${content}`;
      } else if (actionId === 'improveWriting') {
        prompt = `Trau chuốt và nâng tầm cách hành văn của đoạn sau, đảm bảo gãy gọn, sắc bén và súc tích:\n\n${content}`;
      } else if (actionId === 'improveGrammar') {
        prompt = `Sửa tất cả các lỗi ngữ pháp và hoàn thiện câu chữ cho đoạn sau:\n\n${content}`;
      } else if (actionId === 'fixSpelling') {
        prompt = `Kiểm tra và sửa toàn bộ lỗi chính tả trong văn bản sau:\n\n${content}`;
      } else if (actionId === 'makeLonger') {
        prompt = `Mở rộng và làm sâu sắc thêm các luận điểm chiến lược trong đoạn sau với cấu trúc rõ ràng:\n\n${content}`;
      } else if (actionId === 'makeShorter') {
        prompt = `Cô đọng và chắt lọc các điểm mấu chốt nhất từ đoạn sau thành các gạch đầu dòng súc tích:\n\n${content}`;
      } else if (actionId === 'continueWriting') {
        prompt = `Viết tiếp một cách mượt mà và logic mạch ý của văn bản sau:\n\n${content}`;
      } else if (actionId === 'brainstormMindmap') {
        prompt = `Brainstorm các nhánh chiến lược và ý tưởng sáng tạo mở rộng cho chủ đề sau:\n\n${content}`;
      }
    }

    const groqStream = streamGroqToAffineUI(prompt);
    if (stream) {
      return {
        async *[Symbol.asyncIterator]() {
          for await (const chunk of stripThinkingFromStream(groqStream, signal)) {
            if (signal?.aborted) return;
            yield chunk;
          }
        },
      };
    }
    return (async () => {
      let full = '';
      for await (const chunk of groqStream) {
        if (signal?.aborted) break;
        full += chunk;
      }
      return cleanDocText(full);
    })();
  }

  let messageId: string | undefined;

  if (stream) {
    return {
      [Symbol.asyncIterator]: async function* () {
        try {
          if (!retry) {
            messageId = await createMessage({
              client,
              sessionId,
              content,
              attachments,
              params,
              timeout,
              signal,
            });
          }
          if (signal?.aborted) {
            return;
          }
          const byokLeaseId = await createWorkspaceByokLocalLease(
            client,
            workspaceId
          ).catch(() => undefined);
          if (signal?.aborted) {
            return;
          }
          const eventSource = client.chatTextStream(
            {
              sessionId,
              messageId,
              reasoning,
              profileId,
              modelId,
              routeTargetId,
              toolsConfig,
              actionId,
              actionVersion,
              runId,
              retry,
              byokLeaseId,
            },
            endpoint
          );

          let onAbort: (() => void) | undefined;
          try {
            if (signal) {
              if (signal.aborted) {
                eventSource.close();
                return;
              }
              onAbort = () => {
                eventSource.close();
              };
              signal.addEventListener('abort', onAbort, { once: true });
            }

            for await (const event of toTextStream(eventSource, {
              timeout,
              signal,
            })) {
              if (event.type === 'message') {
                yield event.data;
              }
            }
          } finally {
            eventSource.close();
            if (signal && onAbort) {
              signal.removeEventListener('abort', onAbort);
            }
          }
        } catch {
          // Cloud endpoint failed/offline -> Stream directly from Cáo Ơi local Groq/OpenRouter engine!
          for await (const chunk of streamGroqToAffineUI(content || '')) {
            if (signal?.aborted) return;
            yield chunk;
          }
        }
      },
    };
  } else {
    return (async function () {
      try {
        if (!retry) {
          messageId = await createMessage({
            client,
            sessionId,
            content,
            attachments,
            params,
            timeout,
            signal,
          });
        }
        if (signal?.aborted) {
          return '';
        }
        const byokLeaseId = await createWorkspaceByokLocalLease(
          client,
          workspaceId
        ).catch(() => undefined);
        if (signal?.aborted) {
          return '';
        }
        const eventSource = client.chatTextStream(
          {
            sessionId,
            messageId,
            reasoning,
            profileId,
            modelId,
            routeTargetId,
            toolsConfig,
            actionId,
            actionVersion,
            runId,
            retry,
            byokLeaseId,
          },
          endpoint
        );

        let onAbort: (() => void) | undefined;
        try {
          if (signal) {
            if (signal.aborted) {
              eventSource.close();
              return '';
            }
            onAbort = () => {
              eventSource.close();
            };
            signal.addEventListener('abort', onAbort, { once: true });
          }

          const messages: string[] = [];
          for await (const event of toTextStream(eventSource, {
            timeout,
            signal,
          })) {
            if (event.type === 'message') {
              messages.push(event.data);
            }
          }

          return messages.join('');
        } finally {
          eventSource.close();
          if (signal && onAbort) {
            signal.removeEventListener('abort', onAbort);
          }
        }
      } catch {
        let full = '';
        for await (const chunk of streamGroqToAffineUI(content || '')) {
          full += chunk;
        }
        return full;
      }
    })();
  }
}

export function toImage({
  content,
  sessionId,
  workspaceId,
  attachments,
  params,
  seed,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint,
  actionId,
  actionVersion,
  runId,
  client,
}: ToImageOptions) {
  let messageId: string | undefined;
  return {
    [Symbol.asyncIterator]: async function* () {
      if (!retry) {
        messageId = await createMessage({
          client,
          sessionId,
          content,
          attachments,
          params,
          timeout,
          signal,
        });
      }
      if (signal?.aborted) {
        return;
      }
      const byokLeaseId = await createWorkspaceByokLocalLease(
        client,
        workspaceId
      );
      if (signal?.aborted) {
        return;
      }
      const eventSource =
        endpoint === Endpoint.Action
          ? client.chatTextStream(
              {
                sessionId,
                messageId,
                actionId,
                actionVersion,
                runId,
                retry,
                byokLeaseId,
              },
              Endpoint.Action
            )
          : client.imagesStream(
              sessionId,
              messageId,
              seed,
              endpoint,
              byokLeaseId
            );

      for await (const event of toTextStream(eventSource, {
        timeout,
        signal,
      })) {
        if (event.type === 'attachment') {
          yield event.data;
        }
      }
    },
  };
}
